import { useQuery } from '@tanstack/react-query';
import { useLocationStore } from '@/features/location/store/location-store';
import { useFilterStore } from '@/features/filters/store/filter-store';
import { chargePointsApi } from '@/features/charge-points/api/charge-points-api';
import type { LatLngBounds } from '@/types/global';

interface UseBestPriceOptions {
  enabled?: boolean;
  radius: number; // in kilometers
}

/**
 * Calculate map bounds from center point and radius in kilometers
 * Uses approximation: 1 degree latitude ≈ 111 km
 */
function calculateBounds(
  center: { lat: number; lng: number },
  radiusKm: number
): LatLngBounds {
  const latDelta = radiusKm / 111; // 1 degree latitude ≈ 111 km
  const lngDelta = radiusKm / (111 * Math.cos((center.lat * Math.PI) / 180)); // Adjust for longitude at this latitude

  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
}

/**
 * Hook to find best prices near map center
 * Uses max zoom to get single pins (not clusters) and applies current filters
 */
export function useBestPrice({ enabled = true, radius }: UseBestPriceOptions) {
  const mapCenter = useLocationStore((s) => s.mapCenter);
  const getApiFilters = useFilterStore((s) => s.getApiFilters);

  return useQuery({
    queryKey: [
      'best-price',
      mapCenter,
      radius,
      JSON.stringify(getApiFilters()),
    ],
    queryFn: async () => {
      if (!mapCenter) {
        throw new Error('No map center available');
      }

      // Calculate bounds based on radius around map center
      const bounds = calculateBounds(mapCenter, radius);

      // Use max zoom (21) to get single pins instead of clusters
      const MAX_ZOOM = 21;

      // Fetch charge points with current filters
      const data = await chargePointsApi.getChargePointsForMap({
        bounds,
        center: mapCenter,
        zoom: MAX_ZOOM,
        filters: getApiFilters(),
      });

      return data;
    },
    enabled: enabled && !!mapCenter,
    staleTime: 0, // Always fetch fresh data when searching
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
